import React, { useEffect, useRef, useState } from "react";
import MenuItem from "./MenuItem";
import {
  SideMenuWrapper,
  MenuWrapper,
  StyledNavLink,
  SubMenuWrapper,
  Separator,
} from "./styles";
import { getMenuItems } from "../../utils/MenuContant";
import { paths } from "../../utils/constants";
import { useLocation, useNavigate } from "react-router-dom";
import { useNavigationBlock } from "../../context/useBlockNavigationContext";
import { useSidemenu } from "../../context/useSideMenuContext";
import { useCloudPContent } from "../../context/useCPContentContext";
import { deepCompare } from "../../utils";
import { useContent } from "../../context/useContentContext";
import { usePreviewContext } from "../../context/usePreviewContext";
import { useImageData } from "../../context/useImageDataContext";
import { checkHasCPFilledFields, checkHasOTTFilledFields } from "../../utils/formFieldUtils";

interface SideMenuProps {
  activeRoute: string;
  setActiveRoute: React.Dispatch<React.SetStateAction<string>>;
  children?: React.ReactNode;
  closeMenu?: () => void;
}

const SideMenu: React.FC<SideMenuProps> = ({
  activeRoute,
  setActiveRoute,
  children,
  closeMenu,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const openSubmenuPath = useRef<string | null>(paths.METRICS_DASHBOARD);
  const { setIsShowPopUp, setNextRoute } = useNavigationBlock();

  const [isSubmenuOpen, setIsSubmenuOpen] = useState<string | null>(paths.METRICS_DASHBOARD);
  const [openMenuState, setOpenMenuState] = useState<{ [key: string]: boolean }>({
    [paths.METRICS_DASHBOARD]: true
  });
  const [arrowChange, setArrowChange] = useState(true);

  const {
    basicInformation,
    videoData,
    detailMeta,
    tags,
    isEditContent,
    contentForEdit,
    initialEditContent,
    setIsEditContent,


  } = useContent();
  const { imageIds } = useImageData();
  const { previewContentIds } = usePreviewContext();

  const {
    activeSubmenu,
    setActiveSubmenu,
    setIsCreateContent,
    setIsEditCpContent,
    isOTTPlatform,
    isCreateContent,
    isEditCpContent,
  } = useSidemenu();

  const { intialEditContent, cpEditContent, cpBasic, cpVideoId } = useCloudPContent();

  const hasOTTFilledFields = checkHasOTTFilledFields(
    basicInformation,
    videoData,
    detailMeta,
    tags,
    previewContentIds,
    imageIds
  );

  const hasFilledFields = checkHasCPFilledFields(cpBasic, cpVideoId);

  const handleItemClick = (
    path: string,
    hasSubMenu: boolean,
    submenus?: { name: string; path: string }[]
  ) => {
    if (closeMenu) closeMenu();

    if (isCreateContent && hasFilledFields) {
      setNextRoute(path);
      setIsShowPopUp(true);
      return;
    } else if (isEditCpContent && !deepCompare(intialEditContent, cpEditContent)) {
      setNextRoute(path);
      setIsShowPopUp(true);
      return;
    }
    if (isOTTPlatform && isCreateContent && hasOTTFilledFields) {
      setNextRoute(path);
      setIsShowPopUp(true);
      return;
    } else if (isOTTPlatform && isEditContent && !deepCompare(initialEditContent, contentForEdit)) {
      setNextRoute(path);
      setIsShowPopUp(true);
      return;
    }

    if (hasSubMenu && Array.isArray(submenus) && submenus.length > 0) {
      if (openSubmenuPath.current === path) {
        setIsSubmenuOpen(null);
        openSubmenuPath.current = null;
        setArrowChange(false);
        return;
      }

      const activeSubExists = submenus.some(
        (submenu) => submenu.path === activeSubmenu
      );

      setIsSubmenuOpen(path);
      openSubmenuPath.current = path;
      setArrowChange(true);
      setActiveRoute(path);

      if (activeSubExists && activeSubmenu) {
        navigate(activeSubmenu);
      } else {
        const firstSubmenuPath = submenus[0].path;
        setActiveSubmenu(firstSubmenuPath);
        navigate(firstSubmenuPath);
      }
    } else {
      setActiveSubmenu(null);
      openSubmenuPath.current = null;
      setIsSubmenuOpen(null);
      setArrowChange(false);
      setActiveRoute(path);
      navigate(path);
    }

    setIsCreateContent(false);
    setIsEditCpContent(false);
    setIsEditContent(false)
  };

  const handleSubItemClick = (path: string) => {
    if (closeMenu) closeMenu();
    if (isCreateContent && hasFilledFields) {
      setNextRoute(path);
      setIsShowPopUp(true);
      return;
    } else if (isEditCpContent && !deepCompare(intialEditContent, cpEditContent)) {
      setNextRoute(path);
      setIsShowPopUp(true);
      return;
    }
    if (isOTTPlatform && isCreateContent && hasOTTFilledFields) {
      setNextRoute(path);
      setIsShowPopUp(true);
      return;
    } else if (isOTTPlatform && isEditContent && !deepCompare(initialEditContent, contentForEdit)) {
      setNextRoute(path);
      setIsShowPopUp(true);
      return;
    }

    setIsCreateContent(false);
    setIsEditCpContent(false);
    setIsEditContent(false)
    setActiveSubmenu(path);
    navigate(path);
  };

  useEffect(() => {
    const menuItems = getMenuItems(isOTTPlatform);
    const newOpenState: { [key: string]: boolean } = {};
    let newIsSubmenuOpen: string | null = null;
    let newArrowChange = false;

    const normalizedPathname = pathname === "/" ? paths.METRICS_CORE_ENGAGEMENT : pathname;

    const matchedMenu = menuItems.find(
      (item) =>
        item.path === normalizedPathname ||
        item.submenus?.some((submenu) => submenu.path === normalizedPathname)
    );

    if (matchedMenu) {
      setActiveRoute(matchedMenu.path);
      newOpenState[matchedMenu.path] = true;

      const matchedSub = matchedMenu.submenus?.find(
        (submenu) => submenu.path === normalizedPathname
      );

      if (matchedSub) {
        setActiveSubmenu(matchedSub.path);
        newIsSubmenuOpen = matchedMenu.path;
        newArrowChange = true;
        openSubmenuPath.current = matchedMenu.path;
      } else {
        setActiveSubmenu(null);
        if (matchedMenu.submenus && matchedMenu.submenus.length > 0) {
          newIsSubmenuOpen = matchedMenu.path;
          newArrowChange = true;
          openSubmenuPath.current = matchedMenu.path;
        } else {
          openSubmenuPath.current = null;
        }
      }
    } else {
      setActiveRoute(normalizedPathname);
      setActiveSubmenu(null);
    }

    setIsSubmenuOpen(newIsSubmenuOpen);
    setArrowChange(newArrowChange);
    setOpenMenuState(newOpenState);
  }, [pathname, isOTTPlatform]);


  return (
    <>
      <SideMenuWrapper>
        <MenuWrapper>
          {getMenuItems(isOTTPlatform)?.map((item, index) => {
            const hasSubMenu = !!item.submenus && item.submenus.length > 0;
            const isOpen = openMenuState[item.path];

            return (
              <React.Fragment key={item.path}>
                <MenuItem
                  isActive={item.path === activeRoute}
                  text={item.name}
                  icon={item.icon}
                  onClick={() =>
                    handleItemClick(item.path, hasSubMenu, item.submenus)
                  }
                  hasSubMenu={hasSubMenu}
                  isSubmenuOpen={isOpen}
                  arrowChange={arrowChange}
                  isFirstItem={index === 0}
                />
                {hasSubMenu &&
                  isOpen &&
                  arrowChange &&
                  isSubmenuOpen === item.path && (
                    <SubMenuWrapper>
                      {item.submenus.map((subItem, subIndex) =>
                        subItem?.isSeparator ? (
                          <Separator key={`sep-${subIndex}`} />
                        ) : (
                          <StyledNavLink key={subItem.path} className="link">
                            <MenuItem
                              text={subItem.name}
                              isActive={subItem.path === activeSubmenu}
                              onClick={() => handleSubItemClick(subItem.path)}
                              icon={undefined}
                              isSubmenu={true}
                              disabled={(subItem as any).disabled}
                            />
                          </StyledNavLink>
                        )
                      )}
                    </SubMenuWrapper>
                  )}
              </React.Fragment>
            );
          })}
        </MenuWrapper>
      </SideMenuWrapper>
      <main>{children}</main>
    </>
  );
};

export default SideMenu;
